import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Minus } from "lucide-react";
import { api, FALLBACK_PRODUCTS, formatEUR, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const useProductMap = () => {
  const [map, setMap] = useState(Object.fromEntries(FALLBACK_PRODUCTS.map((p) => [p.id, p])));
  useEffect(() => {
    let active = true;
    api.get("/products")
      .then((res) => {
        if (active && Array.isArray(res.data) && res.data.length)
          setMap(Object.fromEntries(res.data.map((p) => [p.id, p])));
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);
  return map;
};

export const CartDrawer = () => {
  const { items, isOpen, closeCart, setQty, remove, clear, count } = useCart();
  const { user } = useAuth();
  const products = useProductMap();
  const [loading, setLoading] = useState(false);
  const [selectedZone, setSelectedZone] = useState("IT");
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const lines = items.map((i) => {
    const product = products[i.id] || FALLBACK_PRODUCTS.find((p) => p.id === i.id);
    return { ...i, product, finish: i.finish || "Ancient Sand" };
  }).filter((l) => l.product);

  const subtotal = lines.reduce((s, l) => s + (l.product?.price || 0) * (l.qty || 1), 0);

  const totalWeight = lines.reduce((sum, l) => {
    const itemWeight = l.product?.weight || 2.0;
    return sum + itemWeight * (l.qty || 1);
  }, 0);

  const checkout = async () => {
    if (!agreedToTerms) {
      toast("Please accept the Terms of Sale & Returns and Privacy Policy to proceed.");
      return;
    }

    if (user === undefined) {
      toast("Verifica della sessione in corso, riprova tra un attimo.");
      return;
    }

    setLoading(true);
    try {
      const checkoutItems = lines.map((l) => ({
        product_id: String(l.product?.id ?? l.id),
        quantity: l.qty,
        finish: l.finish,
        variant: l.product?.variant || null,
        weight: l.product?.weight || 2.0,
      }));

      const { data } = await api.post("/payments/checkout", {
        items: checkoutItems,
        total_weight_kg: totalWeight,
        shipping_zone: selectedZone,
        origin_url: window.location.origin,
        user_email: user?.email || null,
        user_name: user?.name || null, // <-- Passato correttamente a Stripe
      });

      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("URL di checkout non ricevuto dal server.");
      }
    } catch (e) {
      console.error("Checkout Error Details:", e.response?.data);
      toast(formatApiErrorDetail(e.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
            onClick={closeCart} className="drawer-overlay" data-testid="cart-overlay" />
          <motion.aside initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="drawer" data-testid="cart-drawer" aria-label="Shopping cart">
            <div className="flex items-center justify-between pb-6 border-b border-bone/10">
              <p className="label">Your Cart {count > 0 && `(${count})`}</p>
              <button onClick={closeCart} data-testid="cart-close-btn" aria-label="Close cart" className="text-stone hover:text-bone transition-colors duration-300">
                <X className="w-5 h-5" strokeWidth={1.2} />
              </button>
            </div>

            {lines.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-6 text-center" data-testid="cart-empty-state">
                <p className="font-serif text-bone text-3xl font-light">Your cart is empty.</p>
                <p className="body max-w-[240px]">Objects are cast in small numbers — discover what the atelier has poured.</p>
                <a href="#collection" onClick={closeCart} data-testid="cart-explore-link" className="quiet-link">
                  <span>Explore Collection</span><span className="quiet-link-line" />
                </a>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto py-6 space-y-8" data-testid="cart-items">
                  {lines.map(({ product, qty, finish, id }) => (
                    <div key={`${id}-${finish}`} className="flex gap-5" data-testid={`cart-item-${id}-${finish}`}>
                      <img src={product.image} alt={`${product.name} — ${product.variant}`} className="w-20 h-24 object-cover shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="label text-[10px] mb-1">{product.category}</p>
                            <p className="font-serif text-bone text-lg font-light leading-tight">
                              {product.name} <em>— {product.variant}</em>
                            </p>
                            <p className="text-[11px] font-mono text-sand/80 uppercase tracking-wider mt-1.5" data-testid={`cart-item-finish-${id}-${finish}`}>
                              Finish: {finish}
                            </p>
                          </div>
                          <button onClick={() => remove(id, finish)} data-testid={`cart-remove-${id}-${finish}`} aria-label={`Remove ${product.name}`}
                            className="text-stone hover:text-bone transition-colors duration-300">
                            <X className="w-4 h-4" strokeWidth={1.2} />
                          </button>
                        </div>
                        <div className="mt-4 flex items-center justify-between">
                          <div className="qty-stepper" data-testid={`qty-stepper-${id}-${finish}`}>
                            <button onClick={() => setQty(id, finish, Math.max(1, qty - 1))} data-testid={`qty-minus-${id}-${finish}`} aria-label="Decrease quantity"><Minus className="w-3 h-3" strokeWidth={1.4} /></button>
                            <span data-testid={`qty-value-${id}-${finish}`}>{qty}</span>
                            <button onClick={() => setQty(id, finish, qty + 1)} data-testid={`qty-plus-${id}-${finish}`} aria-label="Increase quantity"><Plus className="w-3 h-3" strokeWidth={1.4} /></button>
                          </div>
                          <p className="text-bone text-sm tracking-wider" data-testid={`line-total-${id}-${finish}`}>{formatEUR((product.price || 0) * qty)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-bone/10 pt-6 space-y-5">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-mono text-stone uppercase tracking-wider">
                      Shipping Destination
                    </label>
                    <select
                      value={selectedZone}
                      onChange={(e) => setSelectedZone(e.target.value)}
                      className="w-full bg-slate border border-bone/20 p-2 text-xs text-bone font-mono focus:outline-none focus:border-sand transition-colors"
                    >
                      <option value="IT">Italy</option>
                      <option value="EU">European Union</option>
                      <option value="EXTRA_EU">Europe (Non-EU / CH / UK / NO)</option>
                      <option value="US_CA">USA & Canada</option>
                      <option value="ROW">Rest of World</option>
                    </select>
                  </div>

                  <div className="flex items-center justify-between" data-testid="cart-subtotal">
                    <span className="label">Subtotal</span>
                    <span className="font-serif text-bone text-2xl font-light">{formatEUR(subtotal)}</span>
                  </div>
                  <p className="text-xs text-stone tracking-wide">Shipping calculated automatically at checkout based on destination and weight.</p>
                  
                  {/* Terms & Conditions Checkbox */}
                  <div className="flex items-start gap-3 pt-2 text-xs text-stone font-light">
                    <input 
                      type="checkbox" 
                      id="cart-terms-checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-stone/40 bg-transparent text-bone focus:ring-0 cursor-pointer accent-stone shrink-0"
                      data-testid="cart-terms-checkbox"
                    />
                    <label htmlFor="cart-terms-checkbox" className="cursor-pointer leading-relaxed text-[11px]">
                      I have read and agree to the{" "}
                      <Link to="/terms-and-returns" target="_blank" className="text-bone underline underline-offset-2 hover:text-sand transition-colors">
                        Terms of Sale & Returns
                      </Link>{" "}
                      and{" "}
                      <Link to="/privacy-policy" target="_blank" className="text-bone underline underline-offset-2 hover:text-sand transition-colors">
                        Privacy Policy
                      </Link>.
                    </label>
                  </div>

                  <button 
                    onClick={checkout} 
                    disabled={loading || user === undefined || !agreedToTerms} 
                    data-testid="cart-checkout-btn" 
                    className={`purchase-btn w-full justify-center ${!agreedToTerms ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <span className="relative z-10">{loading ? "Preparing…" : "Checkout"}</span>
                  </button>
                  <button onClick={clear} data-testid="cart-clear-btn" className="text-xs tracking-[0.2em] uppercase text-stone hover:text-bone transition-colors duration-300 w-full text-center">
                    Empty cart
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};